import { useState, useEffect } from 'react';
import { getMergeRequests, approveMergeRequest, rejectMergeRequest } from '../api';
import type { MergeRequest } from '../types';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/Dialog';

const STATUS_META: Record<string, { label: string; bg: string; border: string; text: string }> = {
  pending: { label: 'Pending review', bg: '#FEF3C7', border: '#FDE68A', text: '#92400E' },
  approved: { label: 'Merged', bg: '#D1FAE5', border: '#A7F3D0', text: '#065F46' },
  rejected: { label: 'Rejected', bg: '#FEE2E2', border: '#FECACA', text: '#991B1B' },
};

export default function PatientMergesTab() {
  const [requests, setRequests] = useState<MergeRequest[] | null>(null);
  const [error, setError] = useState('');
  const [rejecting, setRejecting] = useState<number | null>(null);
  const [rejectNotes, setRejectNotes] = useState('');
  const [busyId, setBusyId] = useState<number | null>(null);
  const [confirming, setConfirming] = useState<MergeRequest | null>(null);

  const load = () => getMergeRequests().then(setRequests).catch((e: any) => setError(e.message));
  useEffect(() => { load(); }, []);

  const approve = async (r: MergeRequest) => {
    setConfirming(null);
    setBusyId(r.id); setError('');
    try { await approveMergeRequest(r.id); await load(); } catch (e: any) { setError(e.message); }
    setBusyId(null);
  };
  const reject = async (r: MergeRequest) => {
    try { await rejectMergeRequest(r.id, rejectNotes.trim()); setRejecting(null); setRejectNotes(''); await load(); } catch (e: any) { setError(e.message); }
  };

  if (!requests) return <div style={{ textAlign: 'center', padding: 40, color: '#6B7280' }}>Loading merge requests…</div>;

  return (
    <div>
      <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 16, lineHeight: 1.6 }}>
        Requests staff file from the Verify stage or Patient Records to combine two patient records. Approving re-points every visit, document, bill and history entry from the losing record onto the surviving one.
      </div>
      {error && <div style={{ marginBottom: 14, padding: '8px 12px', background: '#FEE2E2', color: '#991B1B', borderRadius: 'var(--radius)', fontSize: 12 }}>{error}</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {requests.map((r) => {
          const meta = STATUS_META[r.status]!;
          return (
            <Card key={r.id} className="p-3.5">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
                <div>
                  <div style={{ fontSize: 13, marginBottom: 4 }}>
                    <span style={{ fontWeight: 700 }}>{r.loser_name}</span> <span style={{ color: '#9CA3AF' }}>({r.loser_uhid})</span>
                    {' → merges into → '}
                    <span style={{ fontWeight: 700 }}>{r.survivor_name}</span> <span style={{ color: '#9CA3AF' }}>({r.survivor_uhid})</span>
                  </div>
                  {r.reason && <div style={{ fontSize: 12, color: '#6B7280', fontStyle: 'italic' }}>"{r.reason}"</div>}
                  <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 4 }}>Requested by {r.requested_by} on {new Date(r.requested_at).toLocaleString('en-IN')}</div>
                </div>
                <span className="badge" style={{ background: meta.bg, color: meta.text, border: `1px solid ${meta.border}` }}>{meta.label}</span>
              </div>

              {r.status === 'pending' && (
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                  <Button variant="success" size="sm" disabled={busyId === r.id} onClick={() => setConfirming(r)}>
                    {busyId === r.id ? 'Merging…' : '✓ Approve & merge'}
                  </Button>
                  <Button variant="danger" size="sm" onClick={() => { setRejecting(r.id); setRejectNotes(''); }}>✕ Reject</Button>
                </div>
              )}
              {rejecting === r.id && (
                <div style={{ marginTop: 10 }}>
                  <Textarea placeholder="Reason for rejection…" value={rejectNotes} onChange={(e) => setRejectNotes(e.target.value)}
                    className="mb-1.5 h-[60px] text-xs" />
                  <div style={{ display: 'flex', gap: 8 }}>
                    <Button variant="danger" size="sm" onClick={() => reject(r)}>Confirm reject</Button>
                    <Button variant="ghost" size="sm" onClick={() => setRejecting(null)}>Cancel</Button>
                  </div>
                </div>
              )}
              {r.status === 'approved' && r.merge_summary && (
                <div style={{ marginTop: 10, padding: '8px 12px', background: '#F9FAFB', borderRadius: 'var(--radius)', fontSize: 11.5, color: '#6B7280' }}>
                  Moved: {Object.entries(r.merge_summary.moved || {}).filter(([, n]) => n > 0).map(([t, n]) => `${n} ${t}`).join(', ') || 'no rows referenced the losing record'}
                </div>
              )}
              {r.status === 'rejected' && r.review_notes && (
                <div style={{ marginTop: 10, fontSize: 12, color: '#991B1B' }}>Reason: "{r.review_notes}"</div>
              )}
            </Card>
          );
        })}
        {requests.length === 0 && <div style={{ textAlign: 'center', padding: 32, color: '#9CA3AF' }}>No merge requests yet</div>}
      </div>

      <Dialog open={!!confirming} onOpenChange={(open) => !open && setConfirming(null)}>
        <DialogContent className="max-w-[460px]">
          <DialogHeader>
            <DialogTitle>Approve this merge?</DialogTitle>
          </DialogHeader>
          {confirming && (
            <div className="text-[13px] leading-[1.6] text-ink-muted">
              Merge <b className="text-ink">{confirming.loser_name}</b> ({confirming.loser_uhid}) into{' '}
              <b className="text-ink">{confirming.survivor_name}</b> ({confirming.survivor_uhid})? This moves all visits, documents,
              bills and history onto the surviving record and <b>cannot be undone</b> from here.
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirming(null)}>Cancel</Button>
            <Button variant="success" onClick={() => confirming && approve(confirming)}>✓ Approve & merge</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
